<template>
  <section v-editable="blok" class="block block-tool block-tool-quick-access">
    <div class="container container--without-padding">
      <div class="card">
        <div class="card__inner">
          <ClientOnly>
            <MediaCardImage
              :max-width="720"
              :media="audienceImage"
              class="media--card hidden-xs"
            />
          </ClientOnly>
          <div class="card__content">
            <slot />
            <h2 v-if="heading" class="h2">
              {{ heading }}
            </h2>
            <div class="flex flex-col">
              <div class="filter__wrapper">
                <FilterByAudiences
                  v-model="filters['audiences']"
                  :include="selectedAudiences"
                  :is-multi-select="false"
                  :show-selections="true"
                  :use-singular-term="useSingularTerm"
                  label="For"
                  placeholder="Choose an audience"
                />
              </div>
              <div class="filter__wrapper">
                <FilterByTopics
                  v-model="filters['topics']"
                  :include="selectedTopics"
                  :is-multi-select="false"
                  :show-selections="true"
                  label="To help with"
                  placeholder="Choose a topic"
                />
              </div>
              <div v-if="shouldShowStates" class="filter__wrapper">
                <FilterByStates
                  v-model="filters['states']"
                  :is-multi-select="false"
                  :show-selections="true"
                  label="In"
                  placeholder="Any State or Territory"
                />
              </div>
              <button
                :disabled="isBusy"
                class="tool__submit button button--primary button--xl"
                @click="submit"
              >
                <span>
                  {{ cta }}
                </span>
                <i class="icon icon--lg">
                  <Icon name="fa6-solid:arrow-right" />
                </i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { BlockToolQuickAccessStoryblok } from '~/types/storyblok'

const props = withDefaults(
  defineProps<{
    blok: BlockToolQuickAccessStoryblok
    cta?: string
  }>(),
  {
    cta: 'Find Information',
  },
)
const router = useRouter()
const filters = ref({
  states: [],
  topics: [],
  audiences: [],
})
const { audienceImage, isBusy } = await useTool({ ...toRefs(props), filters })
const heading = computed(() => props.blok.heading)
const useSingularTerm = computed(() => props.blok.useSingularTerm)
const selectedAudiences = computed(() => {
  const options =
    props.blok.audiences && props.blok.audiences.length
      ? props.blok.audiences
      : []
  const defaultChoice = props.blok.default_audience
  if (defaultChoice && options.indexOf(defaultChoice)) {
    options.push(defaultChoice)
  }
  return options
})
const selectedTopics = computed(() => {
  const options =
    props.blok.topics && props.blok.topics.length ? props.blok.topics : []
  const defaultChoice = props.blok.default_topic
  if (defaultChoice && options.indexOf(defaultChoice)) {
    options.push(defaultChoice)
  }
  return options
})
const shouldShowStates = computed(() => props.blok.show_states)

onMounted(() => {
  nextTick(() => {
    // set a default audience in model
    filters.value.audiences = props.blok.default_audience
      ? [props.blok.default_audience]
      : []

    // set a default topic in model
    filters.value.topics = props.blok.default_topic
      ? [props.blok.default_topic]
      : []
  })
})

function submit() {
  isBusy.value = true

  const states = filters.value?.states?.join(',') || undefined
  const topics = filters.value?.topics?.join(',') || undefined
  const audiences = filters.value?.audiences?.join(',') || undefined

  router.push({
    path: '/quick-find',
    query: {
      states,
      topics,
      audiences,
    },
  })
}
</script>

<style lang="less">
section.block-tool {
  .card {
    .card__inner {
      .shadow-popover();
      .flex-col();

      @media @tablet {
        .flex-row();
      }

      .media {
        @media @tablet {
          flex-basis: calc(45% - (@gutter-xl / 2));
        }

        @media @desktop {
          flex-basis: calc(50% - (@72px / 2));
        }
      }

      .card__content {
        justify-content: center;
        padding: @gutter;

        @media @tablet {
          padding: @gutter-lg @gutter-lg @gutter-lg @gutter-xl;
        }

        @media @desktop {
          padding: @gutter-xxl @gutter-xxl @gutter-xl @72px;
        }

        .h2 {
          .font-size(23, 30);
          .text-center();

          @media @tablet {
            .text-left();
          }

          @media @desktop {
            .font-size(31, 36);
          }
        }

        .multiselect {
          .multiselect__button-wrapper {
            border: solid @1px @color-fill-muted;
            background-color: @color-fill-alternate;

            button:not(.reset) {
              padding: @3px @gutter @3px 0;

              @media @tablet {
                padding: @gutter-xs @gutter @gutter-xs 0;
              }
            }
          }
        }

        label.label-filters {
          .font-md();

          padding: 0;
          margin-top: @gutter-sm;
          margin-bottom: @gutter-xxs;
        }

        button.tool__submit {
          margin: @gutter 0 0;

          @media @tablet {
            flex: 0 0 auto;
            margin: @gutter-lg 0 0;
            align-self: flex-end;
          }
        }
      }
    }
  }
}
</style>
